import React, {useEffect, useMemo, useRef, useState} from 'react'
import {useTranslation} from 'react-i18next'
import {ActivationRoute, ActivationView} from '../../domain/entities/Activation.ts'
import {useActivationDetail, useNotActivatedActivations} from '../../hooks/useActivations.ts'
import {AgencyRepository} from '../../repositories/AgencyRepository.ts'
import {AuthenticationRepository} from '../../repositories/AuthenticationRepository.ts'
import {GetAgencyByIdUseCase} from '../../domain/useCases/agencies/GetAgencyByIdUseCase.ts'
import {getFlagUrl} from '../../utils/flagUrl.ts'
import CountryISOCode from '../../utils/CountryISOCode.ts'
import {b2bMarketLabel} from '../../utils/b2bMarketDisplay.ts'
import {ActivationCountdown, ActivationRouteBadge, ActivationStatePill, ActivationStepper, ContactFailedFlag, PausedUntilFlag} from './ActivationParts.tsx'
import {availableActivationActions, touchpointLabelKey, useActivationFormatters} from './activationView.ts'
import {ActivationTouchpointsCard} from './ActivationTouchpointsSection.tsx'
import {ActivationActionsMenu} from './ActivationActionsMenu.tsx'
import {useActivationActions} from './useActivationActions.tsx'
import {ActivationSettingsCard} from './ActivationSettingsCard.tsx'
import IconSpinner from '../../assets/icons/spinner.svg?react'

const getAgencyByIdUseCase = new GetAgencyByIdUseCase(new AgencyRepository(new AuthenticationRepository()))

// The raw GET /agencies/{id} fields this page needs (see MyPacksTripbooksContainer
// for why the raw response is read instead of the Agency entity).
type AgencySettingsSnapshot = {
    name?: string
    optInPreTrip?: boolean
    activationReportEmail?: string | null
    contactEmail?: string | null
}

// trip.countries may come as ISO alpha-2 (contract example) or as the numeric
// codes itineraries store: resolve either to a flag and a readable name.
function countryFlag(code: string): {src: string; name: string} | null {
    const iso = CountryISOCode.fromCode(code.toUpperCase())
    return iso ? {src: getFlagUrl(iso.numeric), name: b2bMarketLabel(iso.alpha2Code)} : null
}

type RouteFilter = 'ALL' | ActivationRoute
const ROUTE_FILTERS: RouteFilter[] = ['ALL', 'A', 'B', 'C1']

interface NotActivatedContainerProps {
    agencyId: string
    // Staff reach this page with ?agencyId=: activations are read for that
    // agency and the agency-only settings card is hidden.
    asAdmin?: boolean
    // Deep link from the agency emails: ?activation={id} opens that traveler's
    // detail; &action=contact also opens the contact dialog for it.
    focusActivationId?: string | null
    focusAction?: string | null
}

// Drops the deep-link params from the URL (keeps ?agencyId= for staff).
function clearDeepLinkParams() {
    const params = new URLSearchParams(window.location.search)
    params.delete('activation')
    params.delete('action')
    const query = params.toString()
    window.history.replaceState(window.history.state, '', `${window.location.pathname}${query ? `?${query}` : ''}`)
}

// "No activados": every activation not yet redeemed, all routes, soonest
// deadline first — the page the monthly report email links to. Polls every
// ~60s (useNotActivatedActivations). Responsive at 860px and 560px.
export const NotActivatedContainer: React.FC<NotActivatedContainerProps> = ({agencyId, asAdmin = false, focusActivationId = null, focusAction = null}) => {
    const {t} = useTranslation('dashboard')
    const scope = {agencyId, asAdmin}
    const {activations, isLoadingActivations, isFetchingActivations, activationsError, updatedAt} = useNotActivatedActivations(scope)
    const {handleAction, dialogs, isBusy} = useActivationActions(scope)
    const {formatDay, formatDateTime} = useActivationFormatters()
    const [routeFilter, setRouteFilter] = useState<RouteFilter>('ALL')
    const [agency, setAgency] = useState<AgencySettingsSnapshot | null>(null)

    // The deep-linked activation: fetched by id (with touchpoints) so it shows
    // even when it isn't in the not-activated list; the list row is the
    // fallback until the detail arrives.
    const [focusId, setFocusId] = useState<string | null>(focusActivationId)
    const detailRef = useRef<HTMLElement>(null)
    const hasScrolledRef = useRef(false)
    const hasHandledActionRef = useRef(false)
    useEffect(() => {
        setFocusId(focusActivationId)
        hasScrolledRef.current = false
        hasHandledActionRef.current = false
    }, [focusActivationId])
    const {activationDetail, isLoadingActivationDetail} = useActivationDetail(focusId, scope)
    const focused: ActivationView | null = focusId
        ? (activationDetail ?? activations.find((a) => a.id === focusId) ?? null)
        : null

    useEffect(() => {
        if (!focusId || hasScrolledRef.current || !detailRef.current) return
        hasScrolledRef.current = true
        detailRef.current.scrollIntoView({block: 'start'})
        detailRef.current.focus({preventScroll: true})
    }, [focusId, focused])

    useEffect(() => {
        if (focusAction !== 'contact' || !focused || hasHandledActionRef.current) return
        hasHandledActionRef.current = true
        if (availableActivationActions(focused).includes('editContact')) handleAction('editContact', focused)
    }, [focusAction, focused, handleAction])

    const closeFocus = () => {
        setFocusId(null)
        clearDeepLinkParams()
    }

    useEffect(() => {
        if (!agencyId) return
        getAgencyByIdUseCase.execute(agencyId)
            .then((a) => setAgency(a as unknown as AgencySettingsSnapshot))
            .catch(console.error)
    }, [agencyId])

    const visible = useMemo(
        () => routeFilter === 'ALL' ? activations : activations.filter((a) => a.route === routeFilter),
        [activations, routeFilter]
    )
    const countByRoute = useMemo(() => {
        const counts: Record<string, number> = {ALL: activations.length}
        for (const a of activations) counts[a.route] = (counts[a.route] ?? 0) + 1
        return counts
    }, [activations])

    const travelerName = (a: ActivationView) =>
        [a.traveler.firstName, a.traveler.lastName].filter(Boolean).join(' ') || t('activation.list.noName')

    const contactLine = (a: ActivationView) => a.traveler.noContact
        ? t('activation.list.noContact')
        : [a.traveler.email, a.traveler.phone].filter(Boolean).join(' · ') || t('activation.list.noContact')

    const renderItem = (a: ActivationView, highlight = a.id === focusId) => (
        <li key={a.id} aria-current={highlight || undefined}
            className={`${highlight ? 'bg-orange-50 ' : ''}grid grid-cols-[minmax(0,1.4fr)_minmax(0,1fr)_minmax(0,0.8fr)_minmax(0,1.2fr)_auto] items-center gap-4 px-5 py-4 border-b border-neutralBlack-100 last:border-0
                       max-[860px]:grid-cols-2 max-[860px]:gap-3 max-[860px]:px-4
                       max-[560px]:grid-cols-1 max-[560px]:gap-2.5`}>
            {/* Traveler + code + trip */}
            <div className="min-w-0 flex flex-col gap-1 max-[860px]:col-span-2 max-[560px]:col-span-1">
                <span className="flex items-center gap-2 min-w-0">
                    <span className="text-[14px] font-semibold text-neutralBlack-900 truncate">{travelerName(a)}</span>
                    <span className="text-[12px] font-medium tracking-wide text-neutralBlack-600 whitespace-nowrap">{a.code}</span>
                </span>
                <span className="text-[12.5px] text-neutralBlack-600 truncate">{contactLine(a)}</span>
                <span className="flex items-center gap-1.5 text-[12px] text-neutralBlack-600 min-w-0">
                    {(a.trip.countries ?? []).slice(0, 4).map((code) => {
                        const flag = countryFlag(code)
                        return flag ? (
                            <img key={code} src={flag.src} alt={flag.name} className="w-[18px] h-3 rounded-sm object-cover shadow-[0_0_0_1px_rgba(0,0,0,.08)]"/>
                        ) : <span key={code}>{code}</span>
                    })}
                    <span className="truncate">{formatDay(a.trip.startDate)} – {formatDay(a.trip.endDate)}</span>
                </span>
            </div>

            {/* Route + state + steps */}
            <div className="flex flex-col gap-1.5 items-start min-w-0">
                <span className="flex items-center gap-2 flex-wrap">
                    <ActivationRouteBadge route={a.route}/>
                    <ActivationStatePill state={a.state}/>
                    {a.flags?.contactFailed && <ContactFailedFlag/>}
                    <PausedUntilFlag activation={a}/>
                </span>
                <ActivationStepper activation={a}/>
            </div>

            {/* Deadline */}
            <div className="flex flex-col gap-0.5 min-w-0">
                <span className="text-[11px] font-medium uppercase tracking-wide text-neutralBlack-600">{t('activation.list.deadline')}</span>
                <ActivationCountdown activation={a}/>
                <span className="text-[12px] text-neutralBlack-600">{formatDay(a.redemptionDeadline)}</span>
            </div>

            {/* Next contact / last result */}
            <div className="flex flex-col gap-0.5 min-w-0 text-[12.5px] text-neutralBlack-700">
                <span className="text-[11px] font-medium uppercase tracking-wide text-neutralBlack-600">{t('activation.list.nextContact')}</span>
                <span className="truncate">
                    {a.next
                        ? `${t(touchpointLabelKey(a.next.touchpointKey), {defaultValue: a.next.touchpointKey})} · ${formatDateTime(a.next.at)}`
                        : t('activation.list.noNext')}
                </span>
                {a.lastTouch && (
                    <span className="truncate text-neutralBlack-600">
                        {t('activation.last')} {t(touchpointLabelKey(a.lastTouch.touchpointKey), {defaultValue: a.lastTouch.touchpointKey})} · {t(`activation.touchStatus.${a.lastTouch.status}`, {defaultValue: a.lastTouch.status})}
                    </span>
                )}
            </div>

            <div className="justify-self-end max-[860px]:justify-self-start">
                <ActivationActionsMenu activation={a} disabled={isBusy} onAction={handleAction}/>
            </div>
        </li>
    )

    return (
        <div className="h-full overflow-y-auto no-scrollbar">
            <div className="flex flex-col gap-4 pb-10">
                <header className="flex items-end gap-4 flex-wrap animate-fade-rise">
                    <div className="flex-1 min-w-[220px] flex flex-col gap-1">
                        <h1 className="text-[22px] max-[560px]:text-[19px] font-bold tracking-tight text-neutralBlack-900">
                            {t('activation.list.title')}
                            {asAdmin && agency?.name ? <span className="font-medium text-neutralBlack-600"> · {agency.name}</span> : null}
                        </h1>
                        <p className="text-[13px] leading-[1.5] text-neutralBlack-600 max-w-[640px]">{t('activation.list.subtitle')}</p>
                    </div>
                    <span aria-live="polite" className="flex items-center gap-2 text-[12px] text-neutralBlack-600">
                        {isFetchingActivations && !isLoadingActivations && <IconSpinner className="size-3.5"/>}
                        {updatedAt > 0 && t('activation.list.updatedAt', {time: new Date(updatedAt).toLocaleTimeString(undefined, {hour: '2-digit', minute: '2-digit'})})}
                    </span>
                </header>

                {focusId && (
                    <section ref={detailRef} tabIndex={-1} aria-labelledby="activation-detail-title"
                             className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm focus:outline-none focus-visible:ring-2 focus-visible:ring-neutralBlack-900">
                        <div className="flex items-center gap-3 px-5 max-[860px]:px-4 pt-4 pb-3 border-b border-neutralBlack-100">
                            <h2 id="activation-detail-title" className="flex-1 text-[15px] font-semibold text-neutralBlack-900">{t('activation.detail.title')}</h2>
                            <button type="button" onClick={closeFocus}
                                    className="h-8 px-3 rounded-full border border-neutralBlack-300 bg-white text-[12.5px] font-semibold text-neutralBlack-900 hover:bg-neutralBlack-100">
                                {t('activation.detail.close')}
                            </button>
                        </div>
                        {focused ? (
                            <>
                                <ul>{renderItem(focused, false)}</ul>
                                <div className="px-5 max-[860px]:px-4 pb-4">
                                    {activationDetail ? (
                                        <ActivationTouchpointsCard activation={activationDetail} header={
                                            <h3 className="text-[11.5px] font-medium tracking-wide uppercase text-neutralBlack-600">{t('activation.touchpoints.title')}</h3>
                                        }/>
                                    ) : isLoadingActivationDetail ? (
                                        <div className="h-14 rounded-xl bg-neutralBlack-50 animate-pulse"/>
                                    ) : null}
                                </div>
                            </>
                        ) : isLoadingActivationDetail || isLoadingActivations ? (
                            <div role="status" aria-label={t('activation.detail.loading')} className="flex items-center justify-center py-10"><IconSpinner className="size-6"/></div>
                        ) : (
                            <p role="alert" className="px-5 py-8 text-center text-[13.5px] text-neutralBlack-600">{t('activation.detail.notFound')}</p>
                        )}
                    </section>
                )}

                {!asAdmin && (
                    <ActivationSettingsCard
                        optInPreTrip={agency?.optInPreTrip}
                        activationReportEmail={agency?.activationReportEmail}
                        contactEmail={agency?.contactEmail}
                        onSaved={(settings) => setAgency((a) => ({...a, ...settings}))}
                    />
                )}

                <section aria-labelledby="not-activated-list-title" className="bg-white border border-neutralBlack-200 rounded-2xl shadow-sm">
                    <div className="flex items-center gap-3 flex-wrap px-5 max-[860px]:px-4 pt-4 pb-3 border-b border-neutralBlack-100">
                        <h2 id="not-activated-list-title" className="flex-1 min-w-[160px] text-[15px] font-semibold text-neutralBlack-900">
                            {t('activation.list.count', {count: activations.length})}
                        </h2>
                        <div role="group" aria-label={t('activation.list.filterByRoute')} className="flex items-center gap-1.5 flex-wrap">
                            {ROUTE_FILTERS.map((route) => (
                                <button key={route} type="button" aria-pressed={routeFilter === route} onClick={() => setRouteFilter(route)}
                                        className={`h-8 px-3 rounded-full text-[12.5px] font-semibold border ${routeFilter === route
                                            ? 'bg-neutralBlack-900 border-neutralBlack-900 text-white'
                                            : 'bg-white border-neutralBlack-300 text-neutralBlack-900 hover:bg-neutralBlack-100'}`}>
                                    {route === 'ALL' ? t('activation.list.allRoutes') : t('activation.route.badge', {route})}
                                    <span className="ml-1.5 opacity-80">{countByRoute[route] ?? 0}</span>
                                </button>
                            ))}
                        </div>
                    </div>

                    {isLoadingActivations ? (
                        <div className="flex items-center justify-center py-16"><IconSpinner className="size-8"/></div>
                    ) : activationsError ? (
                        <p role="alert" className="px-5 py-10 text-center text-[13.5px] text-[#B3261E]">{t('activation.list.error')}</p>
                    ) : visible.length === 0 ? (
                        <p className="px-5 py-10 text-center text-[13.5px] text-neutralBlack-600">{t('activation.list.empty')}</p>
                    ) : (
                        <ul>{visible.map((a) => renderItem(a))}</ul>
                    )}
                </section>
            </div>
            {dialogs}
        </div>
    )
}
